import React, { useState, useRef } from 'react';
import { Camera, Upload, Check, AlertCircle, RefreshCw, X, FileSearch } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { scrapePortfolioScreenshot } from '../services/api';

interface PortfolioScraperProps {
  onSuccess: (holdings: Array<{ symbol: string, shares: number, name?: string }>) => void;
  language?: string;
}

export function PortfolioScraper({ onSuccess, language = 'ko' }: PortfolioScraperProps) {
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Convert to base64
    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = e.target?.result as string;
      await processImage(base64);
    };
    reader.readAsDataURL(file);
  };

  const processImage = async (base64: string) => {
    setIsScanning(true);
    setError(null);
    try {
      const result = await scrapePortfolioScreenshot(base64);
      if (result && result.holdings) {
        onSuccess(result.holdings);
        setIsModalOpen(false);
      } else {
        setError(language === 'ko' ? '스크린샷 분석에 실패했습니다.' : 'Failed to analyze screenshot.');
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsScanning(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setIsModalOpen(true)}
        className="flex items-center gap-2 px-4 py-3 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-emerald-500/20 transition-all group active:scale-95"
      >
        <Camera className="w-4 h-4 group-hover:rotate-12 transition-transform" />
        {language === 'ko' ? '스크린샷으로 가져오기' : 'Import from Screenshot'}
      </button>

      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
            onClick={() => !isScanning && setIsModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="w-full max-w-md bg-[#050505] rounded-[2rem] border border-white/10 shadow-2xl p-8 relative overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-500 via-purple-500 to-blue-500" />
              
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute top-6 right-6 text-white/20 hover:text-white transition-colors"
                disabled={isScanning}
              >
                <X className="w-5 h-5" />
              </button>

              <div className="flex flex-col items-center text-center space-y-6">
                <div className="p-4 bg-emerald-500/10 rounded-2xl border border-emerald-500/20">
                  <FileSearch className="w-8 h-8 text-emerald-400" />
                </div>
                
                <div>
                  <h3 className="text-xl font-black text-white italic uppercase tracking-tight">
                    {language === 'ko' ? '지능형 포트폴리오 스캐너' : 'AI Portfolio Scanner'}
                  </h3>
                  <p className="text-[10px] text-white/30 font-bold uppercase tracking-[0.2em] mt-2 leading-relaxed">
                    {language === 'ko' 
                      ? '증권사 앱의 자산 보유 현황 스크린샷을 업로드하세요.\nAI가 종목명과 수량을 자동으로 추출합니다.' 
                      : 'Upload a screenshot of your brokerage holdings.\nAI will automatically extract symbols and shares.'}
                  </p>
                </div>

                <div className="w-full">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    className="hidden"
                    accept="image/*"
                  />
                  
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isScanning}
                    className={`w-full py-6 border-2 border-dashed rounded-3xl transition-all flex flex-col items-center justify-center gap-3 ${
                      isScanning 
                        ? 'border-purple-500/50 bg-purple-500/5' 
                        : 'border-white/10 hover:border-emerald-500/50 hover:bg-emerald-500/5 bg-white/[0.02]'
                    }`}
                  >
                    {isScanning ? (
                      <>
                        <RefreshCw className="w-8 h-8 text-purple-400 animate-spin" />
                        <span className="text-xs font-black text-purple-400 uppercase tracking-widest animate-pulse">
                          {language === 'ko' ? 'AI 엔진 분석 중...' : 'AI ENGINE ANALYZING...'}
                        </span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 text-white/20" />
                        <span className="text-xs font-black text-white/40 uppercase tracking-widest">
                          {language === 'ko' ? '이미지 파일 선택' : 'SELECT IMAGE FILE'}
                        </span>
                      </>
                    )}
                  </button>
                </div>

                {error && (
                  <div className="w-full p-4 bg-rose-500/10 border border-rose-500/20 rounded-xl flex items-center gap-3">
                    <AlertCircle className="w-4 h-4 text-rose-400 flex-shrink-0" />
                    <p className="text-[10px] font-bold text-rose-400 text-left">{error}</p>
                  </div>
                )}

                <div className="text-[8px] text-white/10 font-mono uppercase tracking-[0.3em]">
                  Powered by Gemini 1.5 Vision Pro
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
